import {
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  Star,
} from "lucide-react";
import {
  useState,
  type FormEvent,
} from "react";

import {
  submitNovaGuestFeedback,
} from "./nova-guest-operations";

const GOOGLE_REVIEW_URL =
  "https://g.page/r/CROCCTU0swhBEBM/review";

const INITIAL_FORM = {
  guestName: "",
  email: "",
  phone: "",
  visitDate: "",
  overallRating: 0,
  foodRating: null as number | null,
  serviceRating: null as number | null,
  atmosphereRating:
    null as number | null,
  highlights: "",
  improvements: "",
  additionalComments: "",
  wouldReturn:
    null as boolean | null,
  permissionToContact: false,
};

function RatingControl({
  label,
  value,
  onChange,
  required = false,
}: {
  label: string;
  value: number | null;
  onChange: (value: number) => void;
  required?: boolean;
}) {
  return (
    <div>
      <p className="mb-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
        {label}
        {required && " *"}
      </p>

      <div
        className="flex gap-2"
        role="radiogroup"
        aria-label={label}
      >
        {[1, 2, 3, 4, 5].map(
          (rating) => (
            <button
              key={rating}
              type="button"
              onClick={() =>
                onChange(rating)
              }
              aria-label={`${rating} out of 5`}
              aria-pressed={
                value === rating
              }
              className={[
                "flex h-10 w-10 items-center justify-center border transition-colors",
                value !== null &&
                rating <= value
                  ? "border-[#9d725c] bg-[#9d725c] text-[#f3ead7]"
                  : "border-[#b5bbae] text-[#9d725c] hover:border-[#9d725c]",
              ].join(" ")}
            >
              <Star
                size={15}
                strokeWidth={1.4}
                fill={
                  value !== null &&
                  rating <= value
                    ? "currentColor"
                    : "none"
                }
              />
            </button>
          ),
        )}
      </div>
    </div>
  );
}

const inputClass =
  "w-full border-b border-[#b5bbae] bg-transparent py-3 text-sm text-[#314a3b] outline-none placeholder:text-[#a2aaa0] focus:border-[#9d725c]";

export function NovaFeedbackPage() {
  const [form, setForm] =
    useState(INITIAL_FORM);
  const [busy, setBusy] =
    useState(false);
  const [sent, setSent] =
    useState(false);
  const [error, setError] =
    useState<string | null>(null);

  function update<
    K extends keyof typeof form,
  >(
    key: K,
    value: (typeof form)[K],
  ) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    if (busy) {
      return;
    }

    if (
      form.overallRating < 1 ||
      form.overallRating > 5
    ) {
      setError(
        "Please select an overall rating.",
      );
      return;
    }

    setBusy(true);
    setError(null);

    const result =
      await submitNovaGuestFeedback(
        form,
      );

    setBusy(false);

    if (!result.ok) {
      setError(
        result.error ||
          "We could not send your feedback.",
      );
      return;
    }

    setSent(true);
  }

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <header className="border-b border-[#b5bbae] px-5 py-5 sm:px-8">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-6">
          <button
            type="button"
            onClick={() => {
              window.location.href = "/";
            }}
            className="flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#718076]"
          >
            <ArrowLeft size={14} />
            Back to Nova
          </button>

          <img
            src="/images/nova/logo2.png"
            onError={(event) => {
              event.currentTarget.src =
                "/images/nova/novatransparent.png";
            }}
            alt="Nova Res Culina"
            className="h-14 w-auto object-contain sm:h-16"
          />
        </div>
      </header>

      <section className="mx-auto grid max-w-[1280px] gap-12 px-5 py-12 sm:px-8 lg:grid-cols-[.8fr_1.2fr] lg:gap-20 lg:py-20">
        <aside className="lg:sticky lg:top-10 lg:self-start">
          <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
            The guest book
          </p>

          <h1 className="mt-6 font-['Cormorant_Garamond'] text-6xl font-light leading-[0.9] sm:text-7xl">
            How was
            <br />
            your table?
          </h1>

          <p className="mt-8 max-w-md text-sm leading-7 text-[#667067]">
            Tell Nova what worked, what
            could be better, and what
            you&apos;d like us to remember
            for next time.
          </p>

          <div className="mt-12 border-t border-[#b5bbae] pt-7">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
              Prefer to leave a public
              review?
            </p>

            <p className="mt-3 max-w-sm text-xs leading-6 text-[#667067]">
              The Google review option is
              available to every guest,
              regardless of the feedback
              shared here.
            </p>

            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-3 border-b border-[#9d725c] pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]"
            >
              Review Nova on Google
              <ExternalLink size={13} />
            </a>
          </div>
        </aside>

        <div className="bg-[#f4efe4] p-6 sm:p-10 lg:p-12">
          {sent ? (
            <div className="flex min-h-[620px] flex-col items-center justify-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#9d725c] text-[#9d725c]">
                <Check
                  size={25}
                  strokeWidth={1.4}
                />
              </span>

              <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
                Thank you
              </p>

              <h2 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-none">
                We&apos;re listening.
              </h2>

              <p className="mt-6 max-w-sm text-sm leading-6 text-[#667067]">
                Your feedback has reached
                the Nova team and will help
                us make the next table even
                better.
              </p>

              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-3 border-b border-[#9d725c] pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]"
              >
                Review Nova on Google
                <ExternalLink size={13} />
              </a>

              <button
                type="button"
                onClick={() => {
                  setForm(INITIAL_FORM);
                  setSent(false);
                }}
                className="mt-7 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#718076]"
              >
                Send another response
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-8"
            >
              <div className="border-b border-[#b5bbae] pb-7">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d725c]">
                  Private feedback
                </p>

                <h2 className="mt-3 font-['Cormorant_Garamond'] text-4xl leading-none">
                  Tell us about your visit.
                </h2>
              </div>

              <RatingControl
                label="Overall experience"
                value={
                  form.overallRating ||
                  null
                }
                onChange={(rating) =>
                  update(
                    "overallRating",
                    rating,
                  )
                }
                required
              />

              <div className="grid gap-8 sm:grid-cols-3">
                <RatingControl
                  label="Food"
                  value={form.foodRating}
                  onChange={(rating) =>
                    update(
                      "foodRating",
                      rating,
                    )
                  }
                />

                <RatingControl
                  label="Service"
                  value={
                    form.serviceRating
                  }
                  onChange={(rating) =>
                    update(
                      "serviceRating",
                      rating,
                    )
                  }
                />

                <RatingControl
                  label="Atmosphere"
                  value={
                    form.atmosphereRating
                  }
                  onChange={(rating) =>
                    update(
                      "atmosphereRating",
                      rating,
                    )
                  }
                />
              </div>

              <label className="block">
                <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                  What did you enjoy?
                </span>

                <textarea
                  rows={3}
                  value={form.highlights}
                  onChange={(event) =>
                    update(
                      "highlights",
                      event.target.value,
                    )
                  }
                  placeholder="Food, service, atmosphere, a particular dish..."
                  className={`${inputClass} resize-none`}
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                  What could we improve?
                </span>

                <textarea
                  rows={3}
                  value={
                    form.improvements
                  }
                  onChange={(event) =>
                    update(
                      "improvements",
                      event.target.value,
                    )
                  }
                  className={`${inputClass} resize-none`}
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                  Anything else?
                </span>

                <textarea
                  rows={3}
                  value={
                    form.additionalComments
                  }
                  onChange={(event) =>
                    update(
                      "additionalComments",
                      event.target.value,
                    )
                  }
                  className={`${inputClass} resize-none`}
                />
              </label>

              <div>
                <p className="mb-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                  Would you visit Nova
                  again?
                </p>

                <div className="flex gap-3">
                  {[
                    ["Yes", true],
                    ["Not sure", false],
                  ].map(
                    ([label, value]) => (
                      <button
                        key={String(label)}
                        type="button"
                        onClick={() =>
                          update(
                            "wouldReturn",
                            value as boolean,
                          )
                        }
                        className={[
                          "border px-5 py-3 text-[9px] font-semibold uppercase tracking-[0.16em]",
                          form.wouldReturn ===
                          value
                            ? "border-[#314a3b] bg-[#314a3b] text-[#f3ead7]"
                            : "border-[#b5bbae] text-[#667067]",
                        ].join(" ")}
                      >
                        {label as string}
                      </button>
                    ),
                  )}
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                    Your name
                    <span className="ml-2 font-normal normal-case tracking-normal">
                      (optional)
                    </span>
                  </span>

                  <input
                    value={form.guestName}
                    onChange={(event) =>
                      update(
                        "guestName",
                        event.target.value,
                      )
                    }
                    className={inputClass}
                  />
                </label>

                <label className="block">
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                    Visit date
                    <span className="ml-2 font-normal normal-case tracking-normal">
                      (optional)
                    </span>
                  </span>

                  <input
                    type="date"
                    value={form.visitDate}
                    onChange={(event) =>
                      update(
                        "visitDate",
                        event.target.value,
                      )
                    }
                    className={inputClass}
                  />
                </label>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                    Email
                    <span className="ml-2 font-normal normal-case tracking-normal">
                      (optional)
                    </span>
                  </span>

                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) =>
                      update(
                        "email",
                        event.target.value,
                      )
                    }
                    className={inputClass}
                  />
                </label>

                <label className="block">
                  <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
                    Phone
                    <span className="ml-2 font-normal normal-case tracking-normal">
                      (optional)
                    </span>
                  </span>

                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(event) =>
                      update(
                        "phone",
                        event.target.value,
                      )
                    }
                    className={inputClass}
                  />
                </label>
              </div>

              <label className="flex items-start gap-3 border-t border-[#b5bbae] pt-6">
                <input
                  type="checkbox"
                  checked={
                    form.permissionToContact
                  }
                  onChange={(event) =>
                    update(
                      "permissionToContact",
                      event.target.checked,
                    )
                  }
                  className="mt-1"
                />

                <span className="text-xs leading-6 text-[#667067]">
                  Nova may contact me about
                  this feedback using the
                  details I supplied.
                </span>
              </label>

              {error && (
                <div className="border border-[#9d725c]/30 bg-[#9d725c]/8 px-4 py-3 text-xs leading-5 text-[#7d5544]">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="flex w-full items-center justify-between bg-[#314a3b] px-5 py-4 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] transition-colors hover:bg-[#9d725c] disabled:cursor-wait disabled:opacity-60"
              >
                <span>
                  {busy
                    ? "Sending feedback…"
                    : "Send private feedback"}
                </span>

                <ArrowRight size={16} />
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
